import { useEffect, useMemo, useState } from "react";
import { Settings } from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { parseApiErrorObject } from "@/lib/api-error";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface UnmatchedReviewerUser {
  id: number;
  name: string;
  shortCode?: string | null;
  color?: string | null;
  role: string;
  isActive: boolean;
}

interface UnmatchedEmailSettings {
  reviewerIds: number[];
  users: UnmatchedReviewerUser[];
}

interface UnmatchedEmailSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}

/**
 * Admin-only settings for the shared unmatched pool.  This lives in its own
 * dialog so an admin can reach it even when that admin is not one of the
 * currently selected reviewers.
 */
export function UnmatchedEmailSettingsDialog({
  open,
  onOpenChange,
  onSaved,
}: UnmatchedEmailSettingsDialogProps) {
  const { toast } = useToast();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const settingsQuery = useQuery<UnmatchedEmailSettings>({
    queryKey: ["/api/email-notifications/unmatched/settings"],
    enabled: open,
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        "/api/email-notifications/unmatched/settings",
      );
      return response.json() as Promise<UnmatchedEmailSettings>;
    },
  });

  useEffect(() => {
    if (settingsQuery.data) {
      setSelectedIds(settingsQuery.data.reviewerIds);
    }
  }, [settingsQuery.data]);

  const selectableUsers = useMemo(
    () =>
      (settingsQuery.data?.users || []).filter(
        (candidate) => candidate.role === "team" || candidate.role === "admin",
      ),
    [settingsQuery.data?.users],
  );
  const activeSelectedCount = selectableUsers.filter(
    (candidate) => candidate.isActive && selectedIds.includes(candidate.id),
  ).length;

  const settingsMutation = useMutation({
    mutationFn: async (reviewerIds: number[]) => {
      const response = await apiRequest(
        "PUT",
        "/api/email-notifications/unmatched/settings",
        { reviewerIds },
      );
      return response.json() as Promise<UnmatchedEmailSettings>;
    },
    onSuccess: (data) => {
      setSelectedIds(data.reviewerIds);
      queryClient.setQueryData(
        ["/api/email-notifications/unmatched/settings"],
        data,
      );
      queryClient.invalidateQueries({
        queryKey: ["/api/email-notifications/unmatched"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/email-notifications/unmatched/count"],
      });
      onSaved?.();
      toast({
        title: "Unmatched email settings saved",
        description: "The reviewer selection now applies to unmatched emails.",
      });
    },
  });

  const toggleUser = (id: number, checked: boolean | "indeterminate") => {
    if (checked === "indeterminate") return;
    setSelectedIds((current) =>
      checked
        ? Array.from(new Set([...current, id]))
        : current.filter((selectedId) => selectedId !== id),
    );
  };

  const handleSave = () => {
    // Inactive users are displayed for transparency but can never be added
    // back to the server selection.
    const reviewerIds = selectedIds.filter((id) =>
      selectableUsers.some((candidate) => candidate.id === id && candidate.isActive),
    );
    settingsMutation.mutate(reviewerIds);
  };

  const settingsError = settingsQuery.error
    ? (() => {
        const { body } = parseApiErrorObject(settingsQuery.error);
        return body?.error || body?.message ||
          (settingsQuery.error instanceof Error ? settingsQuery.error.message : "Unable to load settings.");
      })()
    : settingsMutation.error
      ? (() => {
          const { body } = parseApiErrorObject(settingsMutation.error);
          return body?.error || body?.message ||
            (settingsMutation.error instanceof Error ? settingsMutation.error.message : "Unable to save settings.");
        })()
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-gray-800 border-gray-600 text-gray-300 max-w-lg"
        data-testid="dialog-unmatched-email-settings"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-200">
            <Settings className="h-4 w-4" />
            Unmatched email reviewers
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-gray-400">
            Selected internal users review every unmatched email. When all selected
            reviewers choose <span className="text-gray-200">I don&apos;t know</span>,
            the email is sent to admins for a decision.
          </p>

          {!settingsQuery.isLoading && !settingsError && activeSelectedCount === 0 && (
            <p
              className="rounded border border-amber-500/60 bg-amber-500/10 p-2 text-xs text-amber-200"
              role="alert"
              data-testid="unmatched-settings-zero-warning"
            >
              No reviewers are selected. Unmatched emails will be sent directly to
              admins; they will not be silently discarded.
            </p>
          )}

          {settingsQuery.isLoading ? (
            <p className="py-4 text-center text-sm text-gray-400">
              Loading reviewer settings...
            </p>
          ) : settingsError ? (
            <p className="rounded border border-red-500/50 bg-red-500/10 p-2 text-sm text-red-200" role="alert">
              {String(settingsError)}
            </p>
          ) : (
            <div
              className="max-h-64 space-y-1 overflow-y-auto rounded border border-gray-700 p-2"
              data-testid="unmatched-settings-users"
            >
              {selectableUsers.map((candidate) => {
                const checked = selectedIds.includes(candidate.id);
                return (
                  <label
                    key={candidate.id}
                    className={`flex items-center gap-3 rounded px-2 py-2 ${
                      candidate.isActive ? "hover:bg-gray-700" : "opacity-50"
                    }`}
                    data-testid={`unmatched-settings-user-${candidate.id}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(value) => toggleUser(candidate.id, value)}
                      disabled={!candidate.isActive || settingsMutation.isPending}
                      aria-label={candidate.name}
                    />
                    <span
                      className="inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-xs font-medium text-white"
                      style={{ backgroundColor: candidate.color || "#6b7280" }}
                    >
                      {candidate.shortCode || candidate.name || `User ${candidate.id}`}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-200">
                      {candidate.name || `User ${candidate.id}`}
                      {!candidate.isActive && (
                        <span className="ml-2 text-xs text-gray-500">(inactive)</span>
                      )}
                    </span>
                  </label>
                );
              })}
              {selectableUsers.length === 0 && (
                <p className="py-3 text-center text-sm text-gray-400">
                  No internal users are available.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={settingsMutation.isPending}
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
            data-testid="button-cancel-unmatched-settings"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={settingsQuery.isLoading || !!settingsQuery.error || settingsMutation.isPending}
            className="bg-blue-600 text-white hover:bg-blue-500"
            data-testid="button-save-unmatched-settings"
          >
            {settingsMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}